import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Camera, KeyRound, Pencil } from 'lucide-react';
import { changePasswordInputSchema, type ChangePasswordInput } from '@underline/shared';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { FloatingField } from '@/components/ui/floating-field';
import { UserAvatar } from '@/components/users/user-avatar';
import { useChangePassword, useUploadMyAvatar } from '@/hooks/use-users';
import { useAuthStore } from '@/stores/auth-store';

interface ForceChangePasswordModalProps {
  open: boolean;
  onDone: () => void;
}

const defaultValues: ChangePasswordInput = { current: '', next: '' };

export function ForceChangePasswordModal({ open, onDone }: ForceChangePasswordModalProps) {
  const user = useAuthStore((s) => s.user);
  const change = useChangePassword();
  const uploadAvatar = useUploadMyAvatar();
  const fileRef = useRef<HTMLInputElement>(null);
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const form = useForm<ChangePasswordInput>({
    resolver: zodResolver(changePasswordInputSchema),
    defaultValues,
  });

  // Gera/revoga a URL de pré-visualização da foto escolhida (foto é opcional).
  useEffect(() => {
    if (!photo) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(photo);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  function handlePick(file: File) {
    if (!file.type.startsWith('image/')) {
      toast.error('Selecione uma imagem');
      return;
    }
    setPhoto(file);
  }

  async function onSubmit(data: ChangePasswordInput) {
    try {
      await change.mutateAsync(data);
      // Foto é opcional: só envia se o usuário escolheu uma. Falha aqui não bloqueia o acesso.
      if (photo) {
        try {
          await uploadAvatar.mutateAsync(photo);
        } catch {
          toast.error('Senha trocada, mas falhou ao enviar a foto. Você pode tentar nos Ajustes.');
        }
      }
      toast.success('Tudo certo! Bem-vindo(a).');
      onDone();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Falha ao alterar';
      toast.error(msg);
    }
  }

  const busy = change.isPending || uploadAvatar.isPending;

  return (
    <BottomSheet open={open} onClose={() => {}} title="Configure seu acesso" hideClose>
      {/* Aviso de segurança — selo + texto */}
      <div className="mb-5 flex items-start gap-3 rounded-2xl bg-primary-soft p-3.5">
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
          <KeyRound className="h-4 w-4" />
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Por segurança, defina uma nova senha antes de continuar. Aproveite e escolha uma foto de
          perfil (opcional).
        </p>
      </div>

      {/* Foto de perfil (opcional) */}
      <div className="mb-5 flex flex-col items-center gap-2">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="group relative rounded-full"
          aria-label="Escolher foto de perfil"
        >
          {preview ? (
            <img
              src={preview}
              alt=""
              className="h-20 w-20 rounded-full object-cover"
            />
          ) : (
            <UserAvatar
              name={user?.name ?? '?'}
              colorHex={user?.color_hex ?? null}
              url={user?.avatar_url ?? null}
              size="xl"
            />
          )}
          <span className="absolute inset-0 grid place-items-center rounded-full bg-black/35 opacity-0 transition group-hover:opacity-100">
            {preview ? (
              <Pencil className="h-4 w-4 text-white" />
            ) : (
              <Camera className="h-5 w-5 text-white" />
            )}
          </span>
        </button>
        <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {preview ? 'Trocar foto' : 'Adicionar foto (opcional)'}
        </span>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="sr-only"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handlePick(f);
            e.target.value = '';
          }}
        />
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FloatingField
          label="Senha temporária"
          type="password"
          autoComplete="current-password"
          error={form.formState.errors.current?.message}
          {...form.register('current')}
        />
        <FloatingField
          label="Nova senha (mín. 6 caracteres)"
          type="password"
          autoComplete="new-password"
          error={form.formState.errors.next?.message}
          {...form.register('next')}
        />
        <button
          type="submit"
          disabled={busy}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-red transition active:scale-[0.97] disabled:opacity-60"
        >
          <KeyRound className="h-4 w-4" />
          {busy ? 'Salvando...' : 'Definir nova senha'}
        </button>
      </form>
    </BottomSheet>
  );
}
